import React from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faShieldHalved, faChartLine, faGraduationCap, faHandshake, faHeart, faLightbulb, faCheck } from "@fortawesome/free-solid-svg-icons";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import useScrollReveal from "../hooks/useScrollReveal";
import "../Styles/Services.css";

function Services() {
  useScrollReveal();

  const services = [
    { 
      icon: faShieldHalved, 
      slug: "life-insurance",
      title: "Life Insurance & Protection", 
      desc: "Term life, whole life, and universal life policies designed to provide your family with financial security when they need it most.", 
      features: ["Term Life Insurance", "Whole Life Insurance", "Universal Life", "Income Protection"] 
    },
    { 
      icon: faChartLine, 
      slug: "retirement-planning",
      title: "Retirement Planning", 
      desc: "Strategic retirement planning that goes beyond basic 401(k) contributions. We design personalized roadmaps for financial independence.", 
      features: ["IRA Optimization", "401(k) Strategy", "Pension Planning", "Social Security Maximization"] 
    },
    { 
      icon: faGraduationCap, 
      slug: "financial-education",
      title: "Financial Education", 
      desc: "Our Real Secrets of Money program teaches practical financial skills that transform how families think about and manage money.", 
      features: ["Budgeting Mastery", "Debt Elimination", "Investment Basics", "Estate Planning 101"] 
    },
    { 
      icon: faHandshake, 
      slug: "business-protection",
      title: "Business Protection", 
      desc: "Comprehensive solutions for business owners including key person insurance, buy-sell agreements, and succession planning.", 
      features: ["Key Person Insurance", "Buy-Sell Agreements", "Business Succession", "Group Benefits"] 
    },
    { 
      icon: faHeart, 
      slug: "legacy-planning",
      title: "Living Benefits & Legacy", 
      desc: "Access your policy's value while still alive, plus multi-generational wealth strategies that protect your family's future.", 
      features: ["Critical Illness Rider", "Chronic Illness Benefits", "Legacy Planning", "Wealth Transfer"] 
    },
    { 
      icon: faLightbulb, 
      slug: "wealth-accumulation",
      title: "Wealth Accumulation", 
      desc: "Tax-efficient growth strategies including our exclusive Kai-Zen plan that helps high-income earners build supplemental retirement income.", 
      features: ["Kai-Zen Plan", "Index Universal Life", "Tax-Free Growth", "Supplemental Retirement"] 
    },
  ];

  return (
    <div className="services-page">
      <Navbar />

      {/* Hero */}
      <section className="page-hero">
        <div className="page-hero-particles">
          <span className="ph-particle ph-particle-1"></span>
          <span className="ph-particle ph-particle-2"></span>
          <span className="ph-particle ph-particle-3"></span>
          <span className="ph-particle ph-particle-4"></span>
          <span className="ph-particle ph-particle-5"></span>
        </div>
        <div className="container">
          <span className="section-tag">Our Services</span>
          <h1 className="page-hero-title">Financial Solutions<br/>Designed Around You</h1>
          <p className="page-hero-subtitle">
            Every family is unique. That's why every financial plan we build is tailored to your specific goals, timeline, and circumstances.
          </p>
        </div>
      </section>

      {/* Services List */}
      <section className="services-list section-padding">
        <div className="container">
          <div className="services-list-grid">
            {services.map((service, index) => (
              <div className="service-block reveal" key={index}>
                <div className="service-block-left">
                  <div className="service-block-header">
                    <span className="service-block-num">0{index + 1}</span>
                    <div className="service-block-icon">
                      <FontAwesomeIcon icon={service.icon} />
                    </div>
                  </div>
                  <h3>{service.title}</h3>
                  <p className="service-block-desc">{service.desc}</p>
                  <Link to={`/services/${service.slug}`} className="service-learn-more">
                    Learn More <FontAwesomeIcon icon={faArrowRight} />
                  </Link>
                </div>
                <div className="service-block-right">
                  <ul className="service-block-features">
                    {service.features.map((f, i) => (
                      <li key={i}>
                        <FontAwesomeIcon icon={faCheck} className="feature-check" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="services-why section-padding reveal">
        <div className="container">
          <div className="services-why-header">
            <span className="section-tag">Why Families Trust Us</span>
            <h2 className="section-title">The Ebenezer Difference</h2>
          </div>
          <div className="services-why-grid">
            <div className="sw-card">
              <div className="sw-card-num">01</div>
              <h4>Education-First Approach</h4>
              <p>We teach you to understand your finances before making any recommendations. Knowledge is power.</p>
            </div>
            <div className="sw-card">
              <div className="sw-card-num">02</div>
              <h4>No Cookie-Cutter Plans</h4>
              <p>Every strategy is custom-built for your specific situation, goals, and risk tolerance.</p>
            </div>
            <div className="sw-card">
              <div className="sw-card-num">03</div>
              <h4>Lifetime Partnership</h4>
              <p>We don't disappear after the sale. We're with you for every milestone and life change.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="services-cta section-padding reveal">
        <div className="container">
          <div className="services-cta-box">
            <h2>Not sure which solution is right for you?</h2>
            <p>Book a free consultation and our team will help identify the best path forward for your specific situation.</p>
            <Link to="/schedule-appointment" className="btn-primary">
              Schedule Free Consultation <FontAwesomeIcon icon={faArrowRight} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Services;
